import PropTypes from "prop-types";
import { removeHTMLTags } from "../../../../../lib/StringFormat";
import { icons } from "../../../../default_images/IconsList";
import { ICON_COLOR, TeambookIcon } from "../../../../default_images/TeambookIcon";
import BookingIcons from "../../../../my_week/BookingIcons";
import { bookingStartTimeText } from "../../../../planner/default_components/planner_right_side_modal/components/StartTimeBlock";
import UsersProfileImage from "../../../../default_components/UsersProfileImage";
import { DateTime } from "luxon";
import lsKeys from "../../../../default_values/defaultKeys";

const WeekLog = ({
  actualLog,
  openLog = () => {},
  projectFor,
  getHeight,
  changeTooltip,
  changeContextMenu,
  occupations,
  activities,
  tasks,
  variant,
  managers = [],
}) => {
  const isBackgroundTransparent = actualLog.approved === false || actualLog.tentative === true;
  const project = projectFor(actualLog);
  const manager = managers.find((manager) => manager.id === project.manager_id);
  const isPastDate = DateTime.fromISO(actualLog.date).startOf("day").ts < DateTime.now().startOf("day").ts;
  const isLightTheme = localStorage.getItem(lsKeys.THEME) === "light";

  const calculateStyle = () => {
    if (variant === "my-week") {
      return {
        border: "1px solid " + projectFor(actualLog).color,
        height: getHeight(actualLog) + "%",
        backgroundColor: actualLog.tentative ? "unset" : projectFor(actualLog).color,
        borderStyle: "2px solid " + projectFor(actualLog).color,
      };
    }

    if (variant === "actuals") {
      return {
        border: "1px solid " + projectFor(actualLog).color,
        height: getHeight(actualLog) + "%",
        backgroundColor: actualLog.approved ? projectFor(actualLog).color : "unset",
        borderStyle: actualLog.created_with === "ui" ? "solid" : "dashed",
        borderWidth: actualLog.created_with === "ui" ? "1px" : "2px",
      };
    }
  };

  const shouldSwitchTheme = (!isLightTheme && isBackgroundTransparent) || (isLightTheme && !isBackgroundTransparent);

  return (
    <div
      onClick={() => openLog(actualLog)}
      style={{
        ...calculateStyle(),
      }}
      key={actualLog.id}
      data-past={isPastDate}
      className={`week-view__day-log pointer ${!isBackgroundTransparent ? "approved" : ""} ${
        variant === "my-week" ? "my-week-booking" : `actuals-log`
      }`}
      onMouseEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        changeTooltip({
          open: true,
          bookingDuration: actualLog.duration,
          comment: actualLog.comment,
          projectName: projectFor(actualLog)?.name || "",
          projectCode: projectFor(actualLog)?.code || "",
          occupation: occupations.find((occupation) => occupation.id === actualLog.task_id)?.name || "",
          activity: activities.find((activity) => activity.id === actualLog.payroll_item_id)?.name || "",
          left: e.currentTarget.getBoundingClientRect().x,
          top: e.currentTarget.getBoundingClientRect().y,
          logWidth: e.currentTarget.getBoundingClientRect().width,
        });
      }}
      onMouseLeave={() => {
        changeTooltip({ open: false });
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        changeContextMenu({
          open: true,
          pos: { x: e.pageX, y: e.pageY },
          log: actualLog,
        });
      }}
    >
      <div className="week-view__log-data">
        <p className="week-view__log-project-name">{projectFor(actualLog).name}</p>

        <p className="week-view__log-duration">{actualLog.duration / 60}h</p>
      </div>

      {tasks.find((task) => task.id === actualLog.task_id) && (
        <div className="week-view__log-data-row">
          <TeambookIcon
            switchTheme={shouldSwitchTheme}
            color={isLightTheme ? ICON_COLOR.ALTERNATIVE : ICON_COLOR.DEFAULT}
            name={icons.ACTUALS_TASK}
          />
          <p className="week-view__log-data-row-text">{tasks.find((task) => task.id === actualLog.task_id).name}</p>
        </div>
      )}

      {variant === "my-week" && (
        <div>
          <BookingIcons
            booking={actualLog}
            color={projectFor(actualLog).color}
            project={projectFor(actualLog)}
            variant={!isLightTheme && "blue"}
            switchTheme={shouldSwitchTheme}
            showComment={false}
            marginBottom={0}
          />
        </div>
      )}

      {actualLog.start_time?.toString() && (
        <div className="week-view__log-data-row" style={{ marginLeft: -1 }}>
          <p
            className="booking__start-time-block"
            style={{
              display: "flex",
            }}
          >
            <TeambookIcon
              style={{ width: "14px", height: "14px" }}
              switchTheme={shouldSwitchTheme}
              name={isLightTheme ? icons.CLOCK : icons.CLOCK_BLUE}
            />
            <p className="booking__start-time-text">{bookingStartTimeText(actualLog)}</p>
          </p>
        </div>
      )}

      {manager && (
        <div className="week-view__log-data-row">
          <div className="my-week__project-manager">
            <UsersProfileImage size={18} user={manager} userImage={manager.avatars?.[20]} className="manager-avatar" />
            <p>{manager.name}</p>
          </div>
        </div>
      )}

      {removeHTMLTags(actualLog.comment) !== "" && (
        <div className="week-view__log-data-row">
          <TeambookIcon
            style={{ marginLeft: 0 }}
            switchTheme={shouldSwitchTheme}
            name={isLightTheme ? icons.BOOKING_COMMENT : icons.ACTUALS_COMMENT}
          />
          <p className="week-view__log-data-row-text italic">{removeHTMLTags(actualLog.comment)}</p>
        </div>
      )}
    </div>
  );
};

WeekLog.propTypes = {
  actualLog: PropTypes.object.isRequired,
  openLog: PropTypes.func,
  projectFor: PropTypes.func.isRequired,
  getHeight: PropTypes.func.isRequired,
  changeTooltip: PropTypes.func.isRequired,
  changeContextMenu: PropTypes.func.isRequired,
  occupations: PropTypes.array.isRequired,
  activities: PropTypes.array.isRequired,
  tasks: PropTypes.array.isRequired,
  variant: PropTypes.string.isRequired,
  managers: PropTypes.array,
};
export default WeekLog;
